export type NetworkErrorKind =
  | 'offline'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'server'
  | 'client'
  | 'unknown'

export interface NetworkErrorState {
  kind: NetworkErrorKind
  title: string
  message: string
  retryable: boolean
  httpStatus?: number
}

export const NETWORK_ERROR_COPY: Record<
  NetworkErrorKind,
  { title: string; message: string; retryable: boolean }
> = {
  offline: {
    title: '目前無網路連線',
    message: '請檢查您的網路連線，恢復後可重試',
    retryable: true,
  },
  timeout: {
    title: '連線逾時',
    message: '伺服器回應時間過長，請稍後再試',
    retryable: true,
  },
  unauthorized: {
    title: '登入已過期',
    message: '請重新登入',
    retryable: false,
  },
  forbidden: {
    title: '沒有存取權限',
    message: '您沒有權限執行此操作，如有疑問請聯絡管理員',
    retryable: false,
  },
  server: {
    title: '伺服器發生錯誤',
    message: '系統暫時無法處理您的請求，請稍後再試',
    retryable: true,
  },
  client: {
    title: '請求發生問題',
    message: '操作無法完成，請確認後再試',
    retryable: false,
  },
  unknown: {
    title: '發生未預期的錯誤',
    message: '請稍後再試，若持續發生請聯絡客服',
    retryable: true,
  },
}

interface ClassifyNetworkErrorInput {
  thrown?: unknown
  response?: Response
  offline?: boolean
  timedOut?: boolean
  serverMessage?: string
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof DOMException && error.name === 'AbortError'
  ) || (
    error instanceof Error && error.name === 'AbortError'
  )
}

function isNavigatorOffline(): boolean {
  if (typeof navigator === 'undefined') return false
  return !navigator.onLine
}

// Max length for a backend-supplied client (4xx) validation message shown verbatim.
export const MAX_CLIENT_MESSAGE_LEN = 200

// The client (4xx) branch is the ONLY path that may show a backend `message`
// verbatim (validation copy is genuinely useful to the user). That single
// passthrough is bounded + content-filtered here so a hostile / proxied / future
// 4xx body can NEVER surface raw stack traces, internal filesystem paths, markup
// (XSS-shaped), control chars, or oversized blobs as user copy. Any failure of a
// safety check returns undefined, and the caller falls back to the canned copy.
function sanitizeClientMessage(message?: string): string | undefined {
  if (typeof message !== 'string') return undefined
  const trimmed = message.trim()
  if (trimmed.length === 0) return undefined
  // length cap
  if (trimmed.length > MAX_CLIENT_MESSAGE_LEN) return undefined
  // control chars / newlines (multi-line => stack-trace-shaped) — codepoint
  // scan avoids embedding raw control bytes in this source file.
  for (let i = 0; i < trimmed.length; i++) {
    const cp = trimmed.charCodeAt(i)
    if (cp < 0x20 || cp === 0x7f) return undefined
  }
  // markup / angle brackets (XSS-shaped)
  if (/[<>]/.test(trimmed)) return undefined
  // filesystem-path or source-location shapes (internal leakage): `/a/b`, `foo.rs:12`
  if (/(^|\s)\/\S+\//.test(trimmed) || /\.[a-z]{1,4}:\d+/i.test(trimmed)) return undefined
  // stack-trace / exception markers
  if (/\bat\s+[\w$.]+\s*\(/.test(trimmed) || /\b(stack\s*trace|traceback|exception|panicked|\bat\s+line)\b/i.test(trimmed)) return undefined
  return trimmed
}

export function classifyNetworkError(input: ClassifyNetworkErrorInput): NetworkErrorState {
  const {
    thrown,
    response,
    offline = false,
    timedOut = false,
    serverMessage,
  } = input

  let kind: NetworkErrorKind = 'unknown'

  if (offline) {
    kind = 'offline'
  } else if (timedOut) {
    kind = 'timeout'
  } else if (response) {
    if (response.status === 401) {
      kind = 'unauthorized'
    } else if (response.status === 403) {
      kind = 'forbidden'
    } else if (response.status >= 500) {
      kind = 'server'
    } else if (response.status >= 400) {
      kind = 'client'
    }
  } else if (thrown) {
    if (isAbortError(thrown)) {
      kind = 'timeout'
    } else if (isNavigatorOffline()) {
      kind = 'offline'
    } else if (thrown instanceof TypeError) {
      kind = 'unknown'
    } else {
      kind = 'unknown'
    }
  }

  const base = NETWORK_ERROR_COPY[kind]
  // Only the client (4xx) branch may show a backend message verbatim, and only
  // after passing the bounded content filter; anything else uses canned copy.
  const safeServerMessage = kind === 'client' ? sanitizeClientMessage(serverMessage) : undefined
  const message = safeServerMessage ?? base.message

  return {
    kind,
    title: base.title,
    message,
    retryable: base.retryable,
    httpStatus: response?.status,
  }
}
